import {
  ShipmentFieldComparison,
  type ShipmentComparisonRow,
} from "./components/shipment-field-comparison";
import { ChevronDown, RefreshCw } from "lucide-react";
import { formatShipmentDate } from "./shipment-date-display";
import { useId, useState } from "react";
import {
  historyErrorLabel,
  useShipmentHistory,
  useShipmentHistoryRevision,
  type HistoryValue,
  type ShipmentHistoryRevision,
} from "@/api/shipment-history";
import { Button } from "@heroui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
const labels: Record<string, string> = {
  title: "Shipment changes",
  intro:
    "Past shipment details. Pending requests are not applied changes. Check billing for payment and refund status.",
  open: "View history",
  close: "Close history",
  refresh: "Refresh history",
  older: "Load older history",
  loading: "Loading history…",
  empty: "No changes recorded. Earlier details may not be available.",
  partial:
    "This history is incomplete. Some changes could not be recorded. Current shipment details remain available.",
  partialEmpty: "No saved change entries are available.",
  earlierTitle: "Earlier applied corrections",
  correctionApplied: "Shipment correction applied",
  earlierGap:
    "Complete shipment details were not recorded for this earlier change.",
  earlierMore: "Only the 20 most recent earlier corrections are listed.",
  denied: "History is not available for this account or Shipment.",
  unavailable:
    "History is temporarily unavailable. Current details remain available.",
  error: "History could not be loaded. Try again.",
  retry: "Try again",
  before: "Before",
  after: "Recorded after",
  full: "Recorded shipment details",
  changes: "Changed values",
  clear: "Not provided",
  none: "None",
  yes: "Yes",
  no: "No",
  AVAILABLE: "Available",
  REDACTED: "Redacted under retention policy",
  UNSUPPORTED_SCHEMA: "Recorded version cannot be displayed",
  INVALID_RECORDED_DEFINITION: "Recorded definition unavailable",
  UNAVAILABLE: "Value unavailable",
  NONE: "No recorded predecessor",
  CREATED: "Created",
  APPLIED_CHANGE: "Applied change",
  OBSERVED_BASELINE: "Observed baseline — earlier history unknown",
  UNKNOWN_LEGACY: "Not recorded",
  CAPTURED: "Recorded",
  RETAINED_DISPLAY: "Retained display",
  NOT_APPLICABLE: "Not applicable",
  actor: "Updated by",
  policy: "Authorizing policy",
  request: "Related request",
  review: "Related review",
  operation: "Related operation",
  unknown: "Unknown",
  notRecorded: "Not recorded",
  adjustmentScope: "Adjustment scope",
  orderWide: "Order-wide adjustment",
  orderWideNote:
    "Applies once to the Order. No per-Shipment allocation is established; the amount is not an additional charge for each Shipment.",
  notLoaded: "Open recorded details to see the previous value.",
  noDiff: "A comparison is not available for this update.",
  unchanged: "No shipment detail changes to show.",
  recorded: "Recorded at",
  revision: "Revision",
  ADMIN: "Support",
  CUSTOMER: "Customer",
  CARRIER: "Carrier",
  OPERATOR: "Driver",
  SYSTEM: "Automatic update",
  UNKNOWN: "Unknown",
};
const customerFields = new Set([
  "parties",
  "from",
  "to",
  "return",
  "type",
  "address",
  "line1",
  "line2",
  "city",
  "province",
  "country",
  "postalCode",
  "contact",
  "name",
  "company",
  "phone",
  "email",
  "instructions",
  "deliveryNotes",
  "plannedShipDate",
  "plannedDeliveryDate",
  "packages",
  "index",
  "kind",
  "weightGrams",
  "lengthCm",
  "widthCm",
  "heightCm",
  "description",
  "declaredValueCents",
  "isFragile",
  "requiresCooling",
  "service",
  "selected",
  "options",
  "signatureRequired",
  "insurance",
  "coverageMaxCents",
  "currencyCode",
  "addOns",
  "identity",
  "handlingNotes",
  "fulfillment",
  "dispatch",
  "dropoff",
  "pickup",
  "scheduledDate",
  "slot",
  "startTime",
  "endTime",
  "notes",
  "timeZone",
  "dispatchName",
  "dispatchAddress",
  "carrierDropoffName",
  "carrierDropoffAddress",
  "pickupDate",
  "handoffBy",
  "deliveredFastest",
  "deliveredSlowest",
  "commercial",
  "currency",
  "sellAmountCents",
  "pickupPriceCents",
  "adjustments",
  "reason",
  "amountCents",
  "taxCents",
]);
const moneyFields = new Set([
  "coverageMaxCents",
  "declaredValueCents",
  "sellAmountCents",
  "pickupPriceCents",
  "amountCents",
  "taxCents",
]);
const customerFieldLabels: Record<string, string> = {
  from: "Sender",
  to: "Recipient",
  return: "Return address",
  line1: "Street address",
  line2: "Unit / apartment",
  weightGrams: "Weight",
  lengthCm: "Length (cm)",
  widthCm: "Width (cm)",
  heightCm: "Height (cm)",
  coverageMaxCents: "Coverage limit",
  currencyCode: "Currency",
  declaredValueCents: "Declared value",
  sellAmountCents: "Delivery price",
  pickupPriceCents: "Pickup price",
  amountCents: "Amount",
  taxCents: "Tax",
  commercial: "Charges",
  fulfillment: "Handoff details",
  dispatch: "Dispatch location",
  dropoff: "Drop-off location",
  pickup: "Pickup details",
  scheduledDate: "Pickup date",
  precision: "Date detail",
  dispatchName: "Drop-off location",
  dispatchAddress: "Drop-off address",
  carrierDropoffName: "Drop-off location",
  carrierDropoffAddress: "Drop-off address",
  handoffBy: "Drop-off by",
  deliveredFastest: "Estimated delivery from",
  deliveredSlowest: "Estimated delivery to",
  identity: "Service",
  selected: "Service",
  index: "Package number",
};
const recordedDateFields = new Set([
  "plannedShipDate",
  "plannedDeliveryDate",
  "pickupDate",
  "scheduledDate",
  "deliveredFastest",
  "deliveredSlowest",
]);
const instantFields = new Set(["handoffBy", "readyAt"]);
function timeZoneAtPath(value: HistoryValue | null | undefined, path = "") {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return undefined;
  let zone: string | undefined;
  const fulfillment = value.fulfillment;
  if (
    fulfillment &&
    typeof fulfillment === "object" &&
    !Array.isArray(fulfillment) &&
    typeof fulfillment.timeZone === "string"
  )
    zone = fulfillment.timeZone;
  let node: HistoryValue | undefined = value;
  for (const key of path.split("/").slice(1)) {
    if (!node || typeof node !== "object" || Array.isArray(node)) break;
    if (typeof node.timeZone === "string") zone = node.timeZone;
    node = node[key];
  }
  if (
    node &&
    typeof node === "object" &&
    !Array.isArray(node) &&
    typeof node.timeZone === "string"
  )
    zone = node.timeZone;
  return zone;
}
function visibleCustomerField(key: string, parent?: string) {
  return (
    customerFields.has(key) ||
    (parent === "adjustments" && key === "shipmentId")
  );
}
function visibleCustomerPath(path: string) {
  const parts = path
    .split("/")
    .slice(1)
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"));
  return (
    parts.length > 0 &&
    parts.every(
      (part, index) =>
        /^\d+$/.test(part) ||
        visibleCustomerField(part, parts[index - 1]) ||
        ((part === "value" || part === "precision") &&
          recordedDateFields.has(parts[index - 1]))
    )
  );
}
function definitionCurrency(value: HistoryValue | null | undefined) {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return undefined;
  const commercial = value.commercial;
  if (
    !commercial ||
    typeof commercial !== "object" ||
    Array.isArray(commercial)
  )
    return undefined;
  return typeof commercial.currency === "string" &&
    /^[A-Z]{3}$/.test(commercial.currency)
    ? commercial.currency
    : undefined;
}
function fieldLabel(key: string) {
  return (
    customerFieldLabels[key] ??
    key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/^./, (v) => v.toUpperCase())
  );
}
function RecordedValue({
  value,
  field,
  unknown = false,
  nullLabel,
  currency,
  timeZone,
}: {
  value: HistoryValue;
  field?: string;
  unknown?: boolean;
  nullLabel?: string;
  currency?: string;
  timeZone?: string;
}) {
  const { t: translate, i18n } = useTranslation("app");
  const t = (key: string) =>
    translate(`shipmentHistory.${key}`, { defaultValue: labels[key] ?? key });
  if (value === null)
    return (
      <span className="italic text-muted-foreground">
        {t(nullLabel ?? (unknown ? "UNKNOWN_LEGACY" : "clear"))}
      </span>
    );
  if (typeof value === "boolean") return <span>{t(value ? "yes" : "no")}</span>;
  if (
    typeof value === "string" &&
    field &&
    (recordedDateFields.has(field) || instantFields.has(field))
  ) {
    const display = formatShipmentDate(
      value,
      /^\d{4}-\d{2}-\d{2}$/.test(value) ? "DATE" : "INSTANT",
      i18n?.language ?? "en",
      timeZone
    );
    return <span>{display ?? t("UNAVAILABLE")}</span>;
  }
  if (typeof value !== "object") {
    let display = String(value);
    if (typeof value === "number" && field && moneyFields.has(field)) {
      display = currency
        ? new Intl.NumberFormat(i18n?.language === "zh" ? "zh-CN" : "en-CA", {
            style: "currency",
            currency,
          }).format(value / 100)
        : (value / 100).toFixed(2);
    } else if (typeof value === "number" && field === "weightGrams")
      display = `${value / 1000} kg`;
    else if (typeof value === "number" && field === "index")
      display = String(value);
    else if (field === "precision")
      display = translate(`shipmentHistory.datePrecision.${value}`, {
        defaultValue: value === "DATE" ? "Date only" : "Date and time",
      });
    else if (field === "type" || field === "kind") {
      display = translate(`shipmentHistory.values.${value}`, {
        defaultValue: String(value)
          .toLowerCase()
          .replace(/_/g, " ")
          .replace(/^./, (v) => v.toUpperCase()),
      });
    }
    return <span className="whitespace-pre-wrap break-words">{display}</span>;
  }
  if (
    !Array.isArray(value) &&
    typeof value.value === "string" &&
    (value.precision === "DATE" || value.precision === "INSTANT")
  ) {
    const display = formatShipmentDate(
      value.value,
      value.precision,
      i18n?.language ?? "en",
      timeZone
    );
    return <span>{display ?? t("UNAVAILABLE")}</span>;
  }
  if (
    !Array.isArray(value) &&
    value.value === null &&
    ["UNKNOWN_LEGACY", "UNAVAILABLE", "NOT_APPLICABLE", "REDACTED"].includes(
      String(value.availability)
    )
  ) {
    return (
      <span className="text-muted-foreground">
        {t(String(value.availability))}
      </span>
    );
  }
  if (Array.isArray(value))
    return value.length ? (
      <ol className="space-y-3">
        {value.map((item, index) => (
          <li
            key={JSON.stringify([index, item])}
            className="rounded border p-2"
          >
            <RecordedValue
              value={item}
              field={field}
              unknown={unknown}
              nullLabel={nullLabel}
              currency={currency}
              timeZone={timeZone}
            />
          </li>
        ))}
      </ol>
    ) : (
      <span>{t("none")}</span>
    );
  return (
    <dl className="min-w-0">
      {Object.entries(value)
        .filter(([key]) => visibleCustomerField(key, field))
        .map(([key, item]) => (
          <div
            key={key}
            className={
              item !== null && typeof item === "object"
                ? "min-w-0 space-y-2 py-3 first:pt-0"
                : "grid min-w-0 grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-start gap-3 border-b border-default-200/60 py-1.5 last:border-0"
            }
          >
            <dt
              className={
                key === "parties"
                  ? "sr-only"
                  : item !== null && typeof item === "object"
                    ? "text-sm font-semibold text-default-800"
                    : "text-xs font-medium text-default-500"
              }
            >
              {(field === "dispatch" || field === "dropoff") &&
              key === "identity"
                ? translate("shipmentHistory.fields.locationName", {
                    defaultValue: "Location name",
                  })
                : field === "adjustments" && key === "shipmentId"
                  ? t("adjustmentScope")
                  : translate(`shipmentHistory.fields.${key}`, {
                      defaultValue: fieldLabel(key),
                    })}
            </dt>
            <dd className="min-w-0 [overflow-wrap:anywhere]">
              {field === "adjustments" && key === "shipmentId" ? (
                <>
                  <p className="font-medium">
                    {t(item === null ? "orderWide" : "thisShipment")}
                  </p>
                  {item === null && (
                    <p className="text-muted-foreground">
                      {t("orderWideNote")}
                    </p>
                  )}
                </>
              ) : (
                <RecordedValue
                  value={item}
                  currency={
                    typeof value.currencyCode === "string" &&
                    /^[A-Z]{3}$/.test(value.currencyCode)
                      ? value.currencyCode
                      : currency
                  }
                  timeZone={
                    typeof value.timeZone === "string"
                      ? value.timeZone
                      : timeZone
                  }
                  field={key}
                  nullLabel={
                    (field === "adjustments" && key === "taxCents") ||
                    (key === "handlingNotes" &&
                      value.handlingAvailability === "UNKNOWN_LEGACY")
                      ? "notRecorded"
                      : key === "handlingNotes" &&
                          value.handlingAvailability === "CAPTURED"
                        ? "clear"
                        : nullLabel
                  }
                  unknown={
                    unknown ||
                    value.availability === "UNKNOWN_LEGACY" ||
                    value.availability === "UNAVAILABLE"
                  }
                />
              )}
            </dd>
          </div>
        ))}
    </dl>
  );
}
function unknownAtPath(value: HistoryValue | undefined, path: string): boolean {
  let current = value;
  for (const key of path
    .split("/")
    .slice(1)
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))) {
    if (
      current === undefined ||
      current === null ||
      typeof current !== "object"
    )
      return true;
    if (Array.isArray(current)) {
      if (!/^(0|[1-9]\d*)$/.test(key)) return true;
      current = current[Number(key)];
    } else {
      if (current.availability === "UNKNOWN_LEGACY") return true;
      current = current[key];
    }
  }
  return current === undefined;
}
type ComparableField = {
  value: HistoryValue;
  field?: string;
  parent?: string;
  currency?: string;
  nullLabel?: string;
};
function comparisonFields(value: HistoryValue, currency?: string) {
  const result = new Map<string, ComparableField>();
  const visit = (
    node: HistoryValue,
    path: string,
    field?: string,
    parent?: string,
    currentCurrency = currency,
    nullLabel?: string
  ): void => {
    const leaf =
      node === null ||
      typeof node !== "object" ||
      (!Array.isArray(node) &&
        ((typeof node.value === "string" &&
          ["DATE", "INSTANT"].includes(String(node.precision))) ||
          (node.value === null &&
            [
              "UNKNOWN_LEGACY",
              "UNAVAILABLE",
              "NOT_APPLICABLE",
              "REDACTED",
            ].includes(String(node.availability)))));
    if (leaf || (Array.isArray(node) && node.length === 0)) {
      result.set(path, {
        value: node,
        field,
        parent,
        currency: currentCurrency,
        nullLabel,
      });
    } else if (Array.isArray(node)) {
      node.forEach((entry, index) => {
        visit(
          entry,
          `${path}/${index}`,
          field,
          parent,
          currentCurrency,
          nullLabel
        );
      });
    } else if (node !== null && typeof node === "object") {
      const nextCurrency =
        typeof node.currencyCode === "string" &&
        /^[A-Z]{3}$/.test(node.currencyCode)
          ? node.currencyCode
          : currentCurrency;
      Object.entries(node)
        .filter(([key]) => visibleCustomerField(key, field))
        .forEach(([key, entry]) => {
          visit(
            entry,
            `${path}/${key.replace(/~/g, "~0").replace(/\//g, "~1")}`,
            key,
            field,
            nextCurrency,
            key === "handlingNotes" &&
              node.handlingAvailability === "UNKNOWN_LEGACY"
              ? "notRecorded"
              : key === "handlingNotes" &&
                  node.handlingAvailability === "CAPTURED"
                ? "clear"
                : undefined
          );
        });
    }
  };
  visit(value, "");
  return result;
}

function RecordedComparison({
  before,
  after,
}: {
  before: HistoryValue;
  after: HistoryValue;
}) {
  const { t: translate } = useTranslation("app");
  const [showUnchanged, setShowUnchanged] = useState(false);
  const previous = comparisonFields(before, definitionCurrency(before));
  const current = comparisonFields(after, definitionCurrency(after));
  const paths = [...new Set([...previous.keys(), ...current.keys()])];
  const rows: ShipmentComparisonRow[] = paths.map((path) => {
    const old = previous.get(path),
      next = current.get(path);
    const label = path
      .split("/")
      .slice(1)
      .map((key) => key.replace(/~1/g, "/").replace(/~0/g, "~"))
      .filter(
        (key) => !["parties", "address", "contact", "selected"].includes(key)
      )
      .map((key) =>
        /^\d+$/.test(key)
          ? `#${Number(key) + 1}`
          : translate(`shipmentHistory.fields.${key}`, {
              defaultValue: fieldLabel(key),
            })
      )
      .join(" · ");
    const cell = (entry: ComparableField | undefined, root: HistoryValue) =>
      !entry ? (
        translate("shipmentHistory.notRecorded", {
          defaultValue: labels.notRecorded,
        })
      ) : entry.field === "shipmentId" && entry.parent === "adjustments" ? (
        translate(
          `shipmentHistory.${entry.value === null ? "orderWide" : "thisShipment"}`,
          {
            defaultValue:
              labels[entry.value === null ? "orderWide" : "thisShipment"],
          }
        )
      ) : (
        <RecordedValue
          value={entry.value}
          field={entry.field}
          currency={entry.currency}
          timeZone={timeZoneAtPath(root, path)}
          unknown={unknownAtPath(root, path)}
          nullLabel={
            entry.parent === "adjustments" && entry.field === "taxCents"
              ? "notRecorded"
              : entry.nullLabel
          }
        />
      );
    return {
      key: path,
      label,
      changed:
        JSON.stringify(old?.value) !== JSON.stringify(next?.value) ||
        old?.nullLabel !== next?.nullLabel ||
        old?.currency !== next?.currency ||
        unknownAtPath(before, path) !== unknownAtPath(after, path),
      before: cell(old, before),
      after: cell(next, after),
    };
  });
  const changes = rows.filter((row) => row.changed),
    unchanged = rows.filter((row) => !row.changed);
  return (
    <div className="space-y-3">
      <ShipmentFieldComparison
        rows={
          changes.length
            ? [...changes, ...(showUnchanged ? unchanged : [])]
            : unchanged
        }
        fieldLabel={translate("shipmentHistory.field")}
        beforeLabel={translate("shipmentHistory.before", {
          defaultValue: labels.before,
        })}
        afterLabel={translate("shipmentHistory.after", {
          defaultValue: labels.after,
        })}
      />
      {changes.length > 0 && unchanged.length > 0 && (
        <button
          type="button"
          className="min-h-11 text-sm text-primary"
          onClick={() => setShowUnchanged(!showUnchanged)}
        >
          {translate(
            showUnchanged
              ? "shipmentHistory.hideUnchanged"
              : "shipmentHistory.showUnchanged",
            {
              count: unchanged.length,
              defaultValue: showUnchanged
                ? "Hide unchanged fields"
                : `Show unchanged fields (${unchanged.length})`,
            }
          )}
        </button>
      )}
    </div>
  );
}

function Revision({ item }: { item: ShipmentHistoryRevision }) {
  const { t: translate } = useTranslation("app");
  const t = (key: string) =>
    translate(`shipmentHistory.${key}`, { defaultValue: labels[key] ?? key });
  const [expanded, setExpanded] = useState(false);
  const previous = useShipmentHistoryRevision(
    item.shipmentId,
    item.previousRevisionId,
    expanded
  );
  const before = previous.data?.items[0];
  const visibleChanges =
    item.diff.fields?.filter((field) => visibleCustomerPath(field.path)) ?? [];
  const currency = definitionCurrency(item.definition.value);
  const diffRows: ShipmentComparisonRow[] = visibleChanges.map((field) => ({
    key: field.path,
    label: field.path
      .split("/")
      .filter(
        (key, index, parts) =>
          key &&
          !["parties", "address", "contact", "selected"].includes(key) &&
          !(key === "value" && recordedDateFields.has(parts[index - 1]))
      )
      .map((key) =>
        /^\d+$/.test(key)
          ? `#${Number(key) + 1}`
          : translate(`shipmentHistory.fields.${key}`, {
              defaultValue: fieldLabel(key),
            })
      )
      .join(" · "),
    changed: true,
    before: (
      <RecordedValue
        value={field.before}
        currency={definitionCurrency(before?.definition.value)}
        timeZone={timeZoneAtPath(before?.definition.value, field.path)}
        field={
          field.path.endsWith("/value")
            ? field.path.split("/").slice(-2)[0]
            : field.path.split("/").slice(-1)[0]
        }
        nullLabel={!before ? "notLoaded" : undefined}
        unknown={unknownAtPath(
          before?.definition.value ?? undefined,
          field.path
        )}
      />
    ),
    after: (
      <RecordedValue
        value={field.after}
        currency={currency}
        timeZone={timeZoneAtPath(item.definition.value, field.path)}
        field={
          field.path.endsWith("/value")
            ? field.path.split("/").slice(-2)[0]
            : field.path.split("/").slice(-1)[0]
        }
        unknown={unknownAtPath(item.definition.value ?? undefined, field.path)}
      />
    ),
  }));
  return (
    <article className="min-w-0 space-y-3 rounded-lg border p-3">
      <h4 className="font-medium">
        {t(item.kind)} · {t("revision")} {item.sequence}
      </h4>
      <p>
        {t("recorded")}:{" "}
        <time dateTime={item.recordedAt}>
          {new Date(item.recordedAt).toLocaleString()}
        </time>
      </p>
      <p>
        {t("actor")}: {t(item.actor.role)}
      </p>
      {item.definition.availability !== "AVAILABLE" && (
        <p role="status">{t(item.definition.availability)}</p>
      )}
      <h5 className="font-medium">{t("changes")}</h5>
      {item.diff.availability !== "AVAILABLE" ? (
        <p>
          {t("noDiff")} {t(item.predecessorAvailability)}
        </p>
      ) : visibleChanges.length ? (
        <ShipmentFieldComparison
          rows={diffRows}
          fieldLabel={t("field")}
          beforeLabel={t("before")}
          afterLabel={t("after")}
        />
      ) : (
        <p>{t("unchanged")}</p>
      )}
      <details onToggle={(event) => setExpanded(event.currentTarget.open)}>
        <summary className="min-h-11 cursor-pointer py-3 font-medium">
          {t("full")}
        </summary>
        {expanded &&
          (!previous.isError &&
          !previous.isPending &&
          item.previousRevisionId &&
          item.predecessorAvailability === "AVAILABLE" &&
          before?.definition.availability === "AVAILABLE" &&
          before.definition.value &&
          item.definition.availability === "AVAILABLE" &&
          item.definition.value ? (
            <RecordedComparison
              before={before.definition.value}
              after={item.definition.value}
            />
          ) : (
            <div className="grid min-w-0 gap-4 lg:grid-cols-2">
              <section className="min-w-0 rounded-xl border border-default-200 bg-background p-4">
                <h5 className="mb-4 border-b border-default-200 pb-3 font-semibold">
                  {t("before")}
                </h5>
                {!item.previousRevisionId ||
                item.predecessorAvailability !== "AVAILABLE" ? (
                  <p>{t(item.predecessorAvailability)}</p>
                ) : previous.isError ? (
                  <div role="alert">
                    <p>{t(historyErrorLabel(previous.error))}</p>
                    <Action onClick={() => void previous.refetch()}>
                      {t("retry")}
                    </Action>
                  </div>
                ) : previous.isPending ? (
                  <p role="status">{t("loading")}</p>
                ) : before?.definition.availability === "AVAILABLE" &&
                  before.definition.value ? (
                  <RecordedValue
                    value={before.definition.value}
                    currency={definitionCurrency(before.definition.value)}
                    timeZone={timeZoneAtPath(before.definition.value)}
                  />
                ) : (
                  <p>{t(before?.definition.availability ?? "UNAVAILABLE")}</p>
                )}
              </section>
              <section className="min-w-0 rounded-xl border border-default-200 bg-background p-4">
                <h5 className="mb-4 border-b border-default-200 pb-3 font-semibold">
                  {t("after")}
                </h5>
                {item.definition.availability === "AVAILABLE" &&
                item.definition.value ? (
                  <RecordedValue
                    value={item.definition.value}
                    currency={currency}
                    timeZone={timeZoneAtPath(item.definition.value)}
                  />
                ) : (
                  <p>{t(item.definition.availability)}</p>
                )}
              </section>
            </div>
          ))}
      </details>
    </article>
  );
}
export function ShipmentRevisionHistory({
  shipmentId,
  updatedAt,
}: {
  shipmentId: string;
  updatedAt?: string;
}) {
  const { t: translate } = useTranslation("app");
  const t = (key: string) =>
    translate(`shipmentHistory.${key}`, { defaultValue: labels[key] ?? key });
  const [open, setOpen] = useState(false);
  const contentId = useId();
  return (
    <section
      className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-3 border-t border-divider pt-4 text-sm"
      aria-label={t("title")}
    >
      <h3 className="col-start-1 row-start-1 font-semibold">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={open ? contentId : undefined}
          className="flex min-h-11 w-full items-center gap-2 rounded-small text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ChevronDown
            aria-hidden="true"
            size={16}
            className={`shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}
          />
          {t("title")}
        </button>
      </h3>
      {open && (
        <HistoryContent
          key={`${shipmentId}:${updatedAt ?? ""}`}
          shipmentId={shipmentId}
          updatedAt={updatedAt}
          contentId={contentId}
        />
      )}
    </section>
  );
}
function HistoryContent({
  shipmentId,
  updatedAt,
  contentId,
}: {
  contentId: string;
  shipmentId: string;
  updatedAt?: string;
}) {
  const { t: translate, i18n } = useTranslation("app");
  const t = (key: string) =>
    translate(`shipmentHistory.${key}`, { defaultValue: labels[key] ?? key });
  const history = useShipmentHistory(shipmentId, true, updatedAt);
  const earlier = history.data?.pages[0].earlierCorrections;
  const partial = history.data?.pages[0].current.enrolled === false;
  return (
    <>
      <button
        type="button"
        onClick={() => void history.refetch()}
        disabled={history.isFetching}
        aria-busy={history.isFetching}
        aria-label={t("refresh")}
        title={t("refresh")}
        className="col-start-2 row-start-1 inline-flex h-11 w-11 items-center justify-center rounded-medium text-default-500 transition-colors hover:bg-default-100 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait disabled:opacity-50"
      >
        <RefreshCw
          aria-hidden="true"
          size={16}
          className={history.isFetching ? "animate-spin" : ""}
        />
      </button>
      <div id={contentId} className="col-span-2 min-w-0 space-y-3">
        <p className="text-muted-foreground">
          {t(partial ? "partial" : "intro")}
        </p>
        {history.isPending && <p role="status">{t("loading")}</p>}
        {history.isError ? (
          <div role="alert">
            <p>{t(historyErrorLabel(history.error))}</p>
            <Action onClick={() => void history.refetch()}>{t("retry")}</Action>
          </div>
        ) : (
          <>
            {history.data?.pages
              .flatMap((page) => page.items)
              .map((item) => (
                <Revision key={item.id} item={item} />
              ))}
            {!!earlier?.items.length && (
              <section aria-label={t("earlierTitle")} className="space-y-3">
                <h4 className="font-medium">{t("earlierTitle")}</h4>
                {earlier.items.map((receipt) => (
                  <article
                    key={receipt.id}
                    className="space-y-1 rounded-lg border border-divider p-3"
                  >
                    <p className="font-medium">{t("correctionApplied")}</p>
                    <time
                      dateTime={receipt.appliedAt}
                      className="text-default-500"
                    >
                      {new Date(receipt.appliedAt).toLocaleString(
                        i18n?.language ?? "en"
                      )}
                    </time>
                    <p className="text-default-500">{t("earlierGap")}</p>
                  </article>
                ))}
                {earlier.hasMore && (
                  <p className="text-default-500">{t("earlierMore")}</p>
                )}
              </section>
            )}
            {history.data &&
              history.data.pages[0].items.length === 0 &&
              !earlier?.items.length && (
                <p>{t(partial ? "partialEmpty" : "empty")}</p>
              )}
            {history.hasNextPage && (
              <Action
                onClick={() => void history.fetchNextPage()}
                disabled={history.isFetching}
              >
                {t("older")}
              </Action>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Action({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="bordered"
      onPress={onClick}
      isDisabled={disabled}
      className="min-h-11"
    >
      {children}
    </Button>
  );
}
